const UPLOAD_MAX_BYTES = 2000000000;

export const UPLOAD_LIMIT_LABEL = '1ファイル 2GB まで';
export const UPLOAD_TOO_LARGE_MESSAGE = '2GB を超えています';

const IMAGE_UPLOAD_EXTENSIONS = ['png', 'jpg', 'jpeg'];

const PURPOSE_EXTENSIONS: { [purpose in UploadPurpose]: string[] } = {
  'face-drawing': ['dxf', 'pdf', ...IMAGE_UPLOAD_EXTENSIONS],
  'face-image': IMAGE_UPLOAD_EXTENSIONS,
  'frame-drawing': ['dxf'],
  'frame-image': IMAGE_UPLOAD_EXTENSIONS,
};

const REJECT_REASON_LABELS: { [reason in RejectReason]: string } = {
  unsupported: '対応していない形式',
  too_large: UPLOAD_TOO_LARGE_MESSAGE,
  extra_drawing: '背景図面は 1 件のみ添付できます',
  drawing_already_attached: '背景図面は 1 件のみ添付できます',
  images_require_dxf: '関連画像は背景図面が DXF のときだけ追加できます',
  extra_frame: '図枠ファイルは 1 件のみ添付できます',
  frame_already_attached: '図枠ファイルは 1 件のみ添付できます',
  images_require_frame: '関連画像は図枠ファイルを添付してから追加できます',
};

// 上限の 2GB と数え方をそろえるため、1000 ごとに単位を上げる
const BYTE_SIZE_UNITS = ['KB', 'MB', 'GB'];
const BYTE_SIZE_STEP = 1000;

export type UploadPurpose = 'face-drawing' | 'face-image' | 'frame-drawing' | 'frame-image';

export interface UploadSessionRequest {
  purpose: UploadPurpose;
  fileName: string;
  fileSize: number;
}

export interface UploadSessionResponse {
  objectName: string;
  sessionUri: string;
  contentType: string;
}

export type UploadResult = [string, number];

interface UploadProgress {
  fileName: string;
  confirmedBytes: number;
  totalBytes: number;
}

export type UploadProgressHandler = (progress: UploadProgress) => void;

// 何件目かは1から数える
export interface UploadFormProgress extends UploadProgress {
  fileIndex: number;
  fileCount: number;
}

export type RejectReason =
  | 'unsupported'
  | 'too_large'
  | 'extra_drawing'
  | 'drawing_already_attached'
  | 'images_require_dxf'
  | 'extra_frame'
  | 'frame_already_attached'
  | 'images_require_frame';

export interface RejectedFile {
  file: File;
  reason: RejectReason;
}

// ファイル選択欄の accept に渡す「.dxf,.pdf」の形の文字列
export function buildAcceptAttribute(purpose: UploadPurpose): string {
  return PURPOSE_EXTENSIONS[purpose].map((extension) => '.' + extension).join(',');
}

export function isAllowedUploadExtension(purpose: UploadPurpose, fileName: string): boolean {
  const extension = getUploadFileExtension(fileName);
  return extension !== null && PURPOSE_EXTENSIONS[purpose].includes(extension);
}

// 「.」が先頭や末尾にある名前は拡張子なしとする
export function getUploadFileExtension(fileName: string): string | null {
  const dotIndex = fileName.lastIndexOf('.');
  if (dotIndex > 0 && dotIndex < fileName.length - 1) {
    return fileName.slice(dotIndex + 1).toLowerCase();
  }
  return null;
}

export function isValidUploadSize(fileSize: number): boolean {
  return Number.isInteger(fileSize) && fileSize >= 0 && fileSize <= UPLOAD_MAX_BYTES;
}

export function buildRejectMessage(rejected: RejectedFile[]): string {
  if (rejected.length === 0) {
    return '';
  }
  const lines = rejected.map((item) => `${item.file.name}(${REJECT_REASON_LABELS[item.reason]})`);
  return `次のファイルは追加できませんでした。\n${lines.join('\n')}`;
}

export class UploadProtocolError extends Error {
  constructor() {
    super('ファイル送信の確認結果が不正です');
    this.name = 'UploadProtocolError';
    Object.setPrototypeOf(this, UploadProtocolError.prototype);
  }
}

// 容量を「8.4 MB」のような読みやすい文字列にする
export function formatByteSize(bytes: number): string {
  if (bytes < BYTE_SIZE_STEP) {
    return `${bytes} B`;
  }
  const unitIndex = selectByteSizeUnitIndex(bytes);
  const tenths = toTenthsOfUnit(bytes, unitIndex);
  return `${Math.floor(tenths / 10)}.${tenths % 10} ${BYTE_SIZE_UNITS[unitIndex]}`;
}

// 四捨五入した結果が 1000 に届く単位は使わず、次の単位で表す
function selectByteSizeUnitIndex(bytes: number): number {
  const fittingIndex = BYTE_SIZE_UNITS.findIndex((unit, index) => toTenthsOfUnit(bytes, index) < BYTE_SIZE_STEP * 10);
  if (fittingIndex >= 0) {
    return fittingIndex;
  }
  return BYTE_SIZE_UNITS.length - 1;
}

// 小数第1位までの値を、誤差が出ないよう 10 倍した整数で求める
function toTenthsOfUnit(bytes: number, unitIndex: number): number {
  return Math.round(bytes / (Math.pow(BYTE_SIZE_STEP, unitIndex + 1) / 10));
}
